import React from 'react'
import clsx from 'clsx'

import { IconClock } from '../../../../../../../components/icons/IconClock'
import { HorseTrainer } from '../../../../../../../components/rider/HorseTrainer/HorseTrainer'
import RideTimeSelector from '../RideTimeSelector'
import { TimeSelector } from '../TimeSelector'
import { OrderOfGoActions } from '../components/OrderOfGoActions'

import { generateName } from '../../../../../../../helpers/helpers'
import { getRideTime } from '../../../../../../../helpers/time'
import { hasClash } from './order-of-go-rider-utils'

import { IBreakRegistrationByDay } from '../../../../../../../models/events/event.interface'
import { IEventTeamMemberInterface } from '../../../../../../../hooks/useEventRiderTeamMembers'
import { FeesTypeItem } from '../../../../../../../models/event-fees/event-fees.interface'

import { IMAGE_CONSTS } from '../../../../../../../const/image-const'
import { HORSE_NAME_DEFAULT } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'

export const getRiderColumns = ({
  eventTeamMembers,
  eventTeamMembersLoading,
  isEditMode,
  updateFirstRiderTimeStaged,
  updateOutOfTurnRiderTimeStaged,
  updateBreakTimeStaged,
  updateOrderOfGoScratchStaged,
  updateScratchStaged,
  deleteBreakStaged,
  onToggleEditMode,
  isSaving,
}: {
  eventTeamMembers: IEventTeamMemberInterface
  eventTeamMembersLoading: boolean
  isEditMode: boolean
  onToggleEditMode: () => void
  updateFirstRiderTimeStaged: (newTime: string, riderId: string) => void
  updateOutOfTurnRiderTimeStaged: (newTime: string, riderId: string) => void
  updateBreakTimeStaged: (h: string, m: string, s: string, id: string) => void
  updateOrderOfGoScratchStaged: (rowId: string, isScratched: boolean) => void
  updateScratchStaged: (rowId: string) => void
  deleteBreakStaged: (breakId: string) => void
  isSaving: boolean
}) => {
  return [
    {
      cell: (info: any) => {
        const row = info.row.original as IBreakRegistrationByDay
        if (row.isBreakDrag) {
          return (
            <div className={'flex items-center gap-4'}>
              <span
                className={
                  'w-[44px] h-[44px] rounded-full flex items-center justify-center bg-[#F6F7FB]'
                }
              >
                <IconClock />
              </span>
              <span className="text-SeabiscuitDark200ThemeColor font-medium text-base flex items-center">
                Break{' '}
                <span className="text-SeabiscuitDark200ThemeColor/80 text-sm mx-1 inline-flex items-center">
                  /
                </span>{' '}
                Drag
              </span>
            </div>
          )
        }

        const isClash = hasClash([row.horseId ?? ''], info.table.options.meta?.clashedIds)

        return (
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full block shrink-0 my-2 overflow-hidden">
              <img
                src={row.riderProfilePicture || IMAGE_CONSTS.PLACEHOLDERS.USER}
                alt="icons"
                className="object-cover w-full h-full rounded-full"
                onError={(e) =>
                  ((e.target as any).src = `https://ui-avatars.com/api/?name=${generateName(
                    row.riderName ?? ''
                  )}&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true`)
                }
              />
            </span>
            <div className="flex flex-col">
              <span
                className={clsx('text-base', {
                  'text-red-600': isClash && isEditMode && !row.isScratched,
                  'text-SeabiscuitDark200ThemeColor': !(isClash && isEditMode && !row.isScratched),
                })}
              >
                {row.riderName}
              </span>
              <span className="text-SeabiscuitDark200ThemeColor/60 text-nr flex items-center">
                {Number(row.backNumber) > 0 ? (
                  <>{row.backNumber}</>
                ) : (
                  <span className=" inline-flex items-center text-SeabiscuitDark200ThemeColor/60 ">
                    No back number
                  </span>
                )}
              </span>
            </div>
          </div>
        )
      },
      id: 'rider',
      header: () => (
        <span className="text-gray-400 font-normal text-[0.875rem] flex items-center">
          <span className="whitespace-nowrap">Rider & Back Number</span>
        </span>
      ),
    },
    {
      cell: (info: any) => {
        const row = info.row.original as IBreakRegistrationByDay
        const isClash = hasClash([row.horseId ?? ''], info.table.options.meta?.clashedIds)

        if (row.isBreakDrag) return <></>

        return (
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full block shrink-0 my-2 overflow-hidden">
              <img
                src={row.horseProfilePicture || IMAGE_CONSTS.PLACEHOLDERS.USER}
                alt="icons"
                className="object-cover w-full h-full rounded-full"
                onError={(e) =>
                  ((e.target as any).src = `https://ui-avatars.com/api/?name=${generateName(
                    row.horseName ?? ''
                  )}&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true`)
                }
              />
            </span>
            <div className="flex flex-col">
              <span
                className={clsx('text-base', {
                  'text-red-600': isClash && isEditMode && !row.isScratched,
                  'text-SeabiscuitDark200ThemeColor': !(isClash && isEditMode && !row.isScratched),
                })}
              >
                {row.horseName || HORSE_NAME_DEFAULT}
              </span>
              <span className="text-SeabiscuitDark200ThemeColor/60 text-nr flex items-center">
                <HorseTrainer
                  eventTeamMembers={eventTeamMembers}
                  eventTeamMembersLoading={eventTeamMembersLoading}
                  rider={info.row.original}
                />
              </span>
            </div>
          </div>
        )
      },
      id: 'horse',
      header: () => (
        <span className="text-gray-400 font-normal text-[0.875rem] flex items-center">
          <span className="whitespace-nowrap">Horse & Trainer</span>
        </span>
      ),
    },
    {
      id: 'class',
      header: () => (
        <span className="text-gray-400 font-normal  text-[0.875rem] flex items-center justify-end pr-2">
          <span className="whitespace-nowrap">Time Increment & Class</span>
        </span>
      ),
      cell: (info: any) => {
        const row = info.row.original as IBreakRegistrationByDay & { fee?: FeesTypeItem }

        const increment = row.fee?.increment || '00:00:00'

        const isClash = hasClash([row.horseId ?? ''], info.table.options.meta?.clashedIds)

        return (
          <div className="flex items-center justify-end gap-3 pr-2 text-right">
            {isEditMode && (
              <div className="flex items-center">
                <OrderOfGoActions
                  row={row}
                  onClickOrderOfGo={async () =>
                    updateOrderOfGoScratchStaged(row.id, !row.orderOfGoScratched)
                  }
                  onClickScratch={async () => updateScratchStaged(row.id)}
                  onRemoveBreak={async () => deleteBreakStaged(row.id)}
                />
              </div>
            )}

            {!row.isBreakDrag && (
              <div className="text-sm flex gap-1">
                <p className="text-SeabiscuitDark200ThemeColor/50 flex flex-col justify-center">
                  {increment}
                </p>
                <span className="text-SeabiscuitDark200ThemeColor/50 flex flex-col justify-center">
                  •
                </span>
                <p
                  className={clsx('text-SeabiscuitDark200ThemeColor', {
                    'line-through text-SeabiscuitDark200ThemeColor/50': row.isScratched,
                    'text-red-500 font-medium': isClash && !row.isScratched,
                  })}
                >
                  {row.registrationByDayName}
                  {row.fee?.order ? (
                    <span className="m-1 whitespace-nowrap text-[14px] text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
                      {row.fee.order}
                    </span>
                  ) : null}
                </p>
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'order',
      header: () => (
        <span className="text-gray-400 font-normal text-[0.875rem] flex justify-center items-center ">
          <span className="whitespace-nowrap">Order</span>
        </span>
      ),
      cell: (info: any) => {
        const row = info.row.original as IBreakRegistrationByDay
        const data = info.table.options.data as IBreakRegistrationByDay[]
        const nonOtItems = data.filter((item) => !item.isOutOfTurn)
        const firstItemId = nonOtItems[0]?.id

        const isClash = hasClash([row.horseId ?? ''], info.table.options.meta?.clashedIds)

        const count = nonOtItems.findIndex((item) => item.id === row.id) + 1

        return (
          <div className="flex justify-center">
            <span
              className={clsx('rounded-full text-base flex items-center justify-center w-10 h-10', {
                // clash styling - only in edit mode AND not scratched
                'bg-red-50 text-red-600':
                  isEditMode && isClash && !row.isBreakDrag && !row.isScratched,

                // edit mode styling - if no clash OR if scratched
                'bg-black text-white':
                  isEditMode && firstItemId === row.id && (!isClash || row.isScratched),
                'bg-SeabiscuitDark200ThemeColor/5 text-SeabiscuitDark100ThemeColor/50':
                  isEditMode && firstItemId !== row.id && (!isClash || row.isScratched),

                // view mode styling
                'bg-[#122B460D] text-[#122B46]': !isEditMode && row.isBreakDrag,
                'bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor':
                  !isEditMode && !row.isBreakDrag,
              })}
            >
              {row.isOutOfTurn ? 'OT' : count}
            </span>
          </div>
        )
      },
    },
    {
      header: (info: any) => (
        <div className="text-gray-400 font-normal text-[0.875rem] flex items-center justify-center gap-1">
          {isEditMode ? (
            <>
              <button
                className="text-gray-600 hover:opacity-70 underline underline-offset-2 whitespace-nowrap"
                onClick={onToggleEditMode}
              >
                Cancel
              </button>
              <span>•</span>
              <button
                className="text-SeabiscuitMainThemeColor hover:opacity-70 underline underline-offset-2 whitespace-nowrap disabled:opacity-50"
                onClick={() => info.table.options.meta?.saveStagedChanges?.()}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <span className="whitespace-nowrap">Ride Time</span>
              <span>•</span>
              <button
                className="text-SeabiscuitMainThemeColor hover:opacity-70 underline underline-offset-2 whitespace-nowrap"
                onClick={onToggleEditMode}
              >
                Edit
              </button>
            </>
          )}
        </div>
      ),
      id: 'rideTime',
      cell: (info: any) => {
        const row = info.row.original as IBreakRegistrationByDay
        const data = info.table.options.data as IBreakRegistrationByDay[]
        const firstItemId = data.find((item) => item.isOutOfTurn !== true)?.id

        const isClash = hasClash([row.horseId ?? ''], info.table.options.meta?.clashedIds)
        if (info.row.original.orderOfGoScratched) {
          return (
            <span className="block text-SeabiscuitDark200ThemeColor/50 text-sm w-full text-center">
              Dropped
            </span>
          )
        } else if (row.isBreakDrag && row.id && row.time) {
          const timeArr = `${row.time}`.split(':')
          const h = timeArr[0]
          const m = timeArr[1]
          const s = timeArr[2]

          if (isEditMode) {
            // Edit mode - show TimeSelector
            return (
              <div className="text-nr justify-center w-full flex">
                <TimeSelector
                  h={h}
                  m={m}
                  s={s}
                  saveHandler={(h, m, a) => updateBreakTimeStaged(h, m, a, row.id)}
                />
              </div>
            )
          } else {
            // View mode - show formatted break time
            return (
              <div className="text-SeabiscuitDark200ThemeColor text-nr justify-center w-full flex">
                <p>
                  {h}:{m}:{s}
                </p>
              </div>
            )
          }
        }

        // Rider time editing/display
        const rider = info.row.original.id

        // Only show RideTimeSelector in edit mode
        if (isEditMode) {
          if (firstItemId === row.id) {
            return (
              <div className="text-SeabiscuitDark200ThemeColor text-nr justify-center w-full flex flex-col items-center">
                <RideTimeSelector
                  initialTime={row.rideTime || undefined}
                  saveHandler={(newTime) => updateFirstRiderTimeStaged(newTime, rider)}
                  placeholder={row.rideTime || 'Enter first time'}
                  hasClash={isClash && !row.orderOfGoScratched}
                />
              </div>
            )
          } else if (row.isOutOfTurn) {
            // OT order - allow editing existing ride time
            return (
              <div className="text-SeabiscuitDark200ThemeColor text-nr justify-center w-full flex">
                <RideTimeSelector
                  initialTime={row.rideTime || undefined}
                  saveHandler={(newTime) => updateOutOfTurnRiderTimeStaged(newTime, rider)}
                  placeholder={row.rideTime || 'Set time'}
                  hasClash={isClash && !row.orderOfGoScratched}
                />
              </div>
            )
          }
        }

        // Display mode - Show formatted time or "Not Set"
        return (
          <div
            className={clsx('text-nr justify-center w-full flex', {
              // Add clash styling
              'text-red-600': isClash && isEditMode && !row.isScratched,
              'text-SeabiscuitDark200ThemeColor/50': isEditMode && !(isClash && !row.isScratched),
              'text-SeabiscuitDark200ThemeColor': !isEditMode,
            })}
          >
            <p>
              {info.row.original.orderOfGoScratched ? (
                <span className="block text-SeabiscuitDark200ThemeColor/50  text-sm w-full text-center">
                  Dropped
                </span>
              ) : row.rideTime ? (
                getRideTime(row.rideTime)
              ) : (
                'Not Set'
              )}
            </p>
          </div>
        )
      },
    },
  ]
}
